import apiClient, { setSessionLostHandler } from "api/api-client";
import { REDIRECT_URL } from "constants/api";
import {
  FunctionComponent,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { LoginResponse } from "types/login";
import { User } from "types/user";
import { isSuccessStatus } from "utils/is-success-status";
import { ROUTES } from "constants/routes";
import { useRedirectAfterLoginUrlHandler } from "hooks/use-redirect-after-login-url-handler";
import { useLoading } from "./loading";
import { AuthState } from "types/auth";
import { setCsrfToken } from "utils/csrf-token";
import { fetchUserInfo, convertMeResponseToUser } from "services/user-api.service";
import { isPasswordRotationRequired, isPasswordExpired } from "utils/password-expiry";
import { markSessionExpired } from "utils/session-expiry-notice";
import { refreshScheduler } from "utils/refresh-scheduler";
import { queryClient } from "query-client";

interface AuthProviderProps extends PropsWithChildren {}

const AuthContext = createContext<AuthState>({} as any);

export const useAuth = () => useContext(AuthContext);

const AuthProvider: FunctionComponent<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User>();
  const [isImpersonated, setIsImpersonated] = useState(false);
  const [impersonator, setImpersonator] = useState<User | null>(null);
  const [isDeviceRemembered, setIsDeviceRemembered] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const { reset: resetRedirectAfterLoginUrl } = useRedirectAfterLoginUrlHandler();
  const { addLoader, removeLoader } = useLoading();

  const isAuthenticated = Boolean(user);

  const isPasswordChangeRequired = Boolean(
    user &&
    !isImpersonated &&
    isPasswordRotationRequired(user.role) &&
    isPasswordExpired(user.passwordChangedAt)
  );

  const clearSession = useCallback(() => {
    // Logged-out state must not retain a timer that can refresh the discarded session.
    refreshScheduler.disarm();
    queryClient.clear();
    setUser(undefined);
    setIsImpersonated(false);
    setImpersonator(null);
    setIsDeviceRemembered(false);
    setCsrfToken(null);
  }, []);

  const handleSessionLost = useCallback(() => {
    markSessionExpired();
    clearSession();
    window.location.href = ROUTES.login.home;
  }, [clearSession]);

  const login: AuthState["login"] = async (data) => {
    const res = await apiClient.post<LoginResponse>("/auth/login", data);

    if (res.data.otpRequired && res.data.otpChallenge) {
      return {
        success: true,
        otpRequired: true,
        otpChallenge: res.data.otpChallenge,
        responseStatus: res.status,
      };
    }

    if (res.data.csrfToken) {
      await acceptSession(res.data.csrfToken);
      return { success: true, responseStatus: res.status };
    }

    return { success: false, responseStatus: res.status };
  };

  const acceptSession = async (csrfToken: string) => {
    setCsrfToken(csrfToken);
    await getMe();
  };

  const loginSingleSignOn: AuthState["loginSingleSignOn"] = async (data) => {
    const res = await apiClient.post<LoginResponse>("/sso-authenticate-user", { ssoSession: data });
    if (res.data.csrfToken) {
      await acceptSession(res.data.csrfToken);
      return { success: true };
    }
    return { success: false };
  };

  const loginVIISP: AuthState["loginVIISP"] = async (ssoSessionId) => {
    const res = await apiClient.post<LoginResponse>("/public/viisp/session-exchange", { ssoSessionId });
    if (isSuccessStatus(res.status) && res.data.csrfToken) {
      await acceptSession(res.data.csrfToken);
      return { success: true };
    }
    return { success: false };
  };

  const logout = async () => {
    if (isImpersonated) {
      return endImpersonation();
    }

    resetRedirectAfterLoginUrl();

    try {
      await apiClient.post("/auth/logout");
    } catch {
      // Local logout must not depend on backend availability.
    }

    localStorage.removeItem(REDIRECT_URL);

    clearSession();
    window.location.href = ROUTES.home;
  };

  const endImpersonation = async () => {
    if (!isImpersonated) {
      return;
    }

    await apiClient.post("/admin/impersonate/end");

    window.location.href = ROUTES.home;
  };

  const getMe = useCallback(async () => {
    const isMaintenanceMode = import.meta.env.REACT_APP_MAINTENANCE_MODE === "true";
    if (isMaintenanceMode) {
      return;
    }

    const loaderId = addLoader();

    try {
      const res = await fetchUserInfo();

      if (isSuccessStatus(res.status) && res.data.user?.firstName) {
        setUser(convertMeResponseToUser(res.data.user));
        setIsImpersonated(Boolean(res.data.isImpersonating));
        setImpersonator(res.data.impersonator ? convertMeResponseToUser(res.data.impersonator) : null);
        setIsDeviceRemembered(Boolean(res.data.isDeviceRemembered));
        setCsrfToken(res.data.csrfToken ?? null);
      } else {
        clearSession();
      }
    } catch {
      clearSession();
    } finally {
      removeLoader(loaderId);
    }
  }, [addLoader, removeLoader]);

  useEffect(() => {
    const makeItReady = async () => {
      await getMe();

      setIsReady(true);
    };

    makeItReady();
  }, [getMe]);

  useEffect(() => {
    if (!user) {
      setSessionLostHandler(null);
      return;
    }

    setSessionLostHandler(handleSessionLost);

    return () => setSessionLostHandler(null);
  }, [user, handleSessionLost]);

  const confirmUser: AuthState["confirmUser"] = async (password, token) => {
    const res = await apiClient.post<LoginResponse>(`/admin/users/change-password/${token}`, { password });

    if (isSuccessStatus(res.status) && res.data.csrfToken) {
      await acceptSession(res.data.csrfToken);
      return { success: true };
    }

    return { success: false };
  };

  return (
    <AuthContext
      value={{
        login,
        loginSingleSignOn,
        loginVIISP,
        logout,
        user,
        setUser,
        isReady,
        isAuthenticated,
        isImpersonated,
        impersonator,
        isDeviceRemembered,
        setIsDeviceRemembered,
        isPasswordChangeRequired,
        confirmUser,
        getMe,
        endImpersonation,
      }}
    >
      {children}
    </AuthContext>
  );
};

export default AuthProvider;
